/*
 * Adds a "See More" / "See Less" toggle to the product description. The
 * description is clamped to a line and a half by default; the toggle only
 * appears when the text actually overflows that clamp. Handles both the plain
 * custom description and the variant-grouped descriptions (re-measuring when
 * the visible variant description changes).
 */
(function () {
  const COLLAPSED_CLASS = 'is-collapsed';
  const ENHANCED_CLASS = 'product__description--seemore';
  const LABEL_MORE = 'See More';
  const LABEL_LESS = 'See Less';
  const VISIBLE_LINES = 1.5;

  function visibleContent(description) {
    return (
      description.querySelector('.custom-description-part') ||
      description.querySelector('.variant-description:not(.hide-description)')
    );
  }

  // The true rendered height of a single line of text. Reading the wrapper's
  // computed line-height is unreliable (it resolves to "normal" in Chrome, which
  // parseFloat can't read), so drop a hidden one-line probe into the content: it
  // inherits the exact font and line-height, and its height is one line box.
  function lineHeightOf(content) {
    const probe = document.createElement('span');
    probe.textContent = 'A';
    probe.style.cssText = 'display:inline-block;visibility:hidden;padding:0;border:0;';
    content.appendChild(probe);
    const height = probe.getBoundingClientRect().height;
    content.removeChild(probe);
    if (height > 0) return height;
    const styles = getComputedStyle(content);
    return parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.4;
  }

  function enhance(description) {
    if (description.dataset.seemore) return;
    const initial = visibleContent(description);
    if (!initial || initial.textContent.trim() === '') {
      // No visible description yet (e.g. the first variant belongs to no group).
      // Watch for one to appear (a variant switch toggles hide-description) and
      // enhance then, so the See More toggle is still created rather than
      // leaving the clamped text with no way to expand.
      const pending = new MutationObserver(() => {
        const content = visibleContent(description);
        if (content && content.textContent.trim() !== '') {
          pending.disconnect();
          enhance(description);
        }
      });
      pending.observe(description, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
      return;
    }
    description.dataset.seemore = 'true';
    description.classList.add(ENHANCED_CLASS, COLLAPSED_CLASS);

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'product__description__toggle';
    description.appendChild(toggle);

    const syncLabel = () => {
      toggle.textContent = description.classList.contains(COLLAPSED_CLASS)
        ? description.dataset.seemoreMore || LABEL_MORE
        : description.dataset.seemoreLess || LABEL_LESS;
    };

    toggle.addEventListener('click', () => {
      description.classList.toggle(COLLAPSED_CLASS);
      syncLabel();
    });

    const measure = () => {
      const content = visibleContent(description);
      if (!content) {
        toggle.hidden = true;
        return;
      }
      const maxPx = Math.round(VISIBLE_LINES * lineHeightOf(content));
      description.style.setProperty('--seemore-max', `${maxPx}px`);

      const overflowing = content.scrollHeight > maxPx + 1;
      if (!overflowing) description.classList.remove(COLLAPSED_CLASS);
      toggle.hidden = !overflowing;
      syncLabel();
    };

    measure();
    window.addEventListener('load', measure);
    window.addEventListener('resize', measure);
    // Re-measure when the visible variant description changes.
    new MutationObserver(measure).observe(description, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  function run() {
    // Cancel the CSS reveal fallback now that the script is running.
    document
      .querySelectorAll('.product__description--seemore')
      .forEach((description) => description.classList.add('seemore-ready'));
    document.querySelectorAll('.product__description--seemore').forEach(enhance);
  }

  if (document.readyState !== 'loading') run();
  else document.addEventListener('DOMContentLoaded', run);
})();
